import PropTypes from "prop-types";
import React, { useRef, useState } from "react";
import { has, isString } from "lodash";

import { trackDownload, automaticallyDownloadFile } from "~/utilities/helpers";
import FriendlyCaptcha from "~/components/FriendlyCaptcha";

import { useMarketingContext } from "~/context/MarketingContext";
import usePardotTracking from "~/hooks/marketing/usePardotTracking";

import InlineForm from "~/components/InlineForm";

const maybeGetDocumentId = (url, title) => {
    const downloadPath = "/download/";
    const pathIndex = url.indexOf(downloadPath);
    const toStringId = url.slice(pathIndex + downloadPath.length);
    const id = Number.parseInt(toStringId, 10);
    const isNotANumber = Number.isNaN(id);

    return isNotANumber ? title : `${id}`;
};

const getRunOnSubmitActions = (url, title) => {
    if (!url || !isString(url)) {
        return null;
    }

    if (!title) {
        title = "Inline Form Download";
    }

    const id = maybeGetDocumentId(url, title);

    return {
        downloadFile: {
            doc: { title, url, id },
            isGated: true,
            openInSameTab: true,
        },
        tracking: {
            event: "gaEvent",
            values: {
                action: "Inline Form Download",
                category: "Request Software",
                label: title,
            },
        },
    };
};

const getFormPresetValues = (hiddenFieldValues, runOnSubmitActions) => {
    if (!runOnSubmitActions) {
        return {};
    }

    const title = runOnSubmitActions.downloadFile.doc.title;

    if (has(hiddenFieldValues, "reference_design")) {
        return {
            reference_design: `${hiddenFieldValues.reference_design} - ${title}`,
        };
    }

    return { reference_design: title };
};

const HtmlEmbedForm = ({ form, title = "", url = "", link = "", children }) => {
    const { hiddenFieldValues } = useMarketingContext();
    const captchaRef = useRef(null);
    const [showCaptcha, setShowCaptcha] = useState(false);

    const { onDownloadSubmission } = usePardotTracking();

    const getRunOnSubmitHandler = (runOnSubmitActions) => {
        if (!runOnSubmitActions) {
            return null;
        }

        return async () => {
            trackDownload(
                runOnSubmitActions.tracking.event,
                runOnSubmitActions.tracking.values,
            );

            onDownloadSubmission(runOnSubmitActions.downloadFile.doc);
            setShowCaptcha(true);
        };
    };

    const onCaptchaComplete = (token) => {
        automaticallyDownloadFile(
            runOnSubmitActions.downloadFile.doc,
            runOnSubmitActions.downloadFile.isGated,
            runOnSubmitActions.downloadFile.openInSameTab,
            null,
            token,
        );
        setShowCaptcha(false);
    };

    const runOnSubmitActions = getRunOnSubmitActions(url, title);
    const runOnSubmit = getRunOnSubmitHandler(runOnSubmitActions);
    const formPresetValues = getFormPresetValues(
        hiddenFieldValues,
        runOnSubmitActions,
    );

    return (
        <>
            <InlineForm
                {...{
                    form,
                    title,
                    runOnSubmit,
                    runOnSubmitActions,
                    formPresetValues,
                    asLink: link === "true",
                }}
            >
                {children}
            </InlineForm>
            {showCaptcha ? (
                <FriendlyCaptcha
                    ref={captchaRef}
                    onComplete={onCaptchaComplete}
                    startMode="auto"
                />
            ) : null}
        </>
    );
};

HtmlEmbedForm.propTypes = {
    form: PropTypes.string.isRequired,
    children: PropTypes.string,
    title: PropTypes.string,
    url: PropTypes.string,
    link: PropTypes.string,
};

export default HtmlEmbedForm;
